import styled from "styled-components";
import { CSS_ARMOR_CLASS } from "../constants/css";
import { cssMainSelector } from "../lib/cssSelectors";
import React, { PureComponent } from "react";
import PropTypes from "prop-types";
import OpenSansRegularEot from "../fonts/openSans/open-sans-v15-latin-regular.eot";
import OpenSansRegularTtf from "../fonts/openSans/open-sans-v15-latin-regular.ttf";
import OpenSansRegularWoff from "../fonts/openSans/open-sans-v15-latin-regular.woff";
import OpenSansRegularWoff2 from "../fonts/openSans/open-sans-v15-latin-regular.woff2";
import OpenSansBoldEot from "../fonts/openSans/open-sans-v15-latin-700.eot";
import OpenSansBoldTtf from "../fonts/openSans/open-sans-v15-latin-700.ttf";
import OpenSansBoldWoff from "../fonts/openSans/open-sans-v15-latin-700.woff";
import OpenSansBoldWoff2 from "../fonts/openSans/open-sans-v15-latin-700.woff2";
import OpenSansItalicEot from "../fonts/openSans/open-sans-v15-latin-italic.eot";
import OpenSansItalicTtf from "../fonts/openSans/open-sans-v15-latin-italic.ttf";
import OpenSansItalicWoff from "../fonts/openSans/open-sans-v15-latin-italic.woff";
import OpenSansItalicWoff2 from "../fonts/openSans/open-sans-v15-latin-italic.woff2";
import OpenSansSemiBoldEot from "../fonts/openSans/open-sans-v15-latin-600.eot";
import OpenSansSemiBoldTtf from "../fonts/openSans/open-sans-v15-latin-600.ttf";
import OpenSansSemiBoldWoff from "../fonts/openSans/open-sans-v15-latin-600.woff";
import OpenSansSemiBoldWoff2 from "../fonts/openSans/open-sans-v15-latin-600.woff2";

const ArmorContainer = styled.div`
  ${p => cssMainSelector(p.theme.tdsId, { ampCount: 1, selectorCount: 5 })} {
    @font-face {
      font-family: "Open Sans";
      font-style: normal;
      font-weight: 400;
      src: url(${OpenSansRegularEot});
      src: local("Open Sans Regular"), local("OpenSans-Regular"),
        url(${OpenSansRegularWoff2}) format("woff2"),
        url(${OpenSansRegularWoff}) format("woff"),
        url(${OpenSansRegularTtf}) format("truetype");
    }

    @font-face {
      font-family: "Open Sans";
      font-style: normal;
      font-weight: 600;
      src: url(${OpenSansSemiBoldEot});
      src: local("Open Sans SemiBold"), local("OpenSans-SemiBold"),
        url(${OpenSansSemiBoldWoff2}) format("woff2"),
        url(${OpenSansSemiBoldWoff}) format("woff"),
        url(${OpenSansSemiBoldTtf}) format("truetype");
    }

    @font-face {
      font-family: "Open Sans";
      font-style: normal;
      font-weight: 700;
      src: url(${OpenSansBoldEot});
      src: local("Open Sans Bold"), local("OpenSans-Bold"),
        url(${OpenSansBoldWoff2}) format("woff2"),
        url(${OpenSansBoldWoff}) format("woff"),
        url(${OpenSansBoldTtf}) format("truetype");
    }

    @font-face {
      font-family: "Open Sans";
      font-style: italic;
      font-weight: 400;
      src: url(${OpenSansItalicEot});
      src: local("Open Sans Italic"), local("OpenSans-Italic"),
        url(${OpenSansItalicWoff2}) format("woff2"),
        url(${OpenSansItalicWoff}) format("woff"),
        url(${OpenSansItalicTtf}) format("truetype");
    }

    color: ${p => p.theme.font.color};
    font-family: ${p => p.theme.font.family};
    font-size: ${p => p.theme.font.size};
    letter-spacing: ${p => p.theme.font.letterSpacing};
    text-transform: none;

    /* Optimize font rendering */
    font-feature-settings: "liga";
    text-rendering: optimizeLegibility;
    -moz-osx-font-smoothing: grayscale;
    -webkit-font-smoothing: antialiased;

    /* Basic Reset of elements we use */
    div,
    span,
    a,
    img,
    input,
    dl,
    dt,
    dd,
    ol,
    ul,
    li,
    form,
    label,
    table,
    tbody,
    tfoot,
    thead,
    tr,
    th,
    td {
      border: 0;
      box-sizing: border-box;
      color: ${p => p.theme.font.color};
      float: none;
      margin: 0;
      padding: 0;
      font-family: ${p => p.theme.font.family};
      font-size: ${p => p.theme.font.size};
      font-variant-caps: normal;
      font-weight: ${p => p.theme.font.weightNormal};
      letter-spacing: ${p => p.theme.font.letterSpacing};
      text-align: left;
      text-transform: none;
      vertical-align: baseline;
      word-break: normal;
      position: static;
    }

    /* HTML5 display-role reset for older browsers */
    table {
      border-collapse: collapse;
      border-spacing: 0;
    }

    /* Correct the line height in all browsers. */
    line-height: 1.15; /* 1.1 */
    -ms-text-size-adjust: 100%; /* 1.2 */
    -webkit-text-size-adjust: 100%; /* 1.2 */

    /*
     * Add the correct box sizing in Firefox and show the overflow in Edge and
     * IE.
     */
    hr {
      height: 0; /* 2.1 */
      overflow: visible; /* 2.2 */
    }

    /* Show the overflow in Edge */
    button,
    input {
      overflow: visible;
    }

    /* 5.   Remove the inheritance of text transform in Edge, Firefox, and IE */
    button,
    select {
      text-transform: none;
    }

    /*
     * Prevent a WebKit bug where (2) destroys native
     * \`audio\` and \`video\`controls in Android 4. Also correct the inability
     * to style clickable types in iOS and Safari.
     */
    button,
    [type="button"],
    [type="reset"],
    [type="submit"] {
      -webkit-appearance: button;
    }

    /* 7.   Remove the inner border and padding in Firefox. */
    button::-moz-focus-inner,
    [type="button"]::-moz-focus-inner,
    [type="reset"]::-moz-focus-inner,
    [type="submit"]::-moz-focus-inner {
      border-style: none;
      padding: 0;
    }
    button:-moz-focusring,
    [type="button"]:-moz-focusring,
    [type="reset"]:-moz-focusring,
    [type="submit"]:-moz-focusring {
      outline: 1px dotted ButtonText;
    }
  }
`;

class StyleArmor extends PureComponent {
  render() {
    const { children, themeTdsId } = this.props;

    return (
      // We need this to be able to create more specificity for our CSS,
      // so external CSS does not interfere with our styling.
      <div id={themeTdsId} className={CSS_ARMOR_CLASS}>
        <ArmorContainer>
          {children}
        </ArmorContainer>
      </div>
    );
  }
}

StyleArmor.propTypes = {
  themeTdsId: PropTypes.string.isRequired
};

export default StyleArmor;
